import React from 'react';
import type { LucideIcon } from 'lucide-react';

/**
 * EmptyState — the ONE shared "nothing here yet" placeholder of the Aurora
 * Cockpit design system (`.da-empty`; see src/styles/auroraCockpit.css).
 *
 * Icon well + Newsreader title + muted body + optional action slot, on a
 * dashed border with a subtle primary-tinted radial top so it reads as
 * cockpit, not a void. Fully token-driven → themes across dark/light + all
 * 4 accents with zero overrides.
 *
 * Lives under ui/aurora/ alongside the other round-2 cockpit primitives, kept
 * separate from the legacy Overview-UX `ui/status-pill.tsx` etc. so the two
 * systems coexist during the surface-migration wave.
 */
export interface EmptyStateProps {
  /** Lucide icon shown in the icon well. */
  icon: LucideIcon;
  /** Newsreader display title (e.g. "No reports yet"). */
  title: string;
  /** Muted explanatory body copy. */
  body?: React.ReactNode;
  /** Optional action slot (typically a primary button). */
  action?: React.ReactNode;
  /** Custom children to render below the body */
  children?: React.ReactNode;
  className?: string;
  'data-testid'?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon,
  title,
  body,
  action,
  children,
  className = '',
  'data-testid': testId,
}) => (
  <div className={`da-empty ${className}`.trim()} data-testid={testId}>
    <span className="da-empty__icon">
      <Icon className="w-5 h-5" aria-hidden />
    </span>
    <div className="da-empty__title">{title}</div>
    {body ? <p className="da-empty__body">{body}</p> : null}
    {children}
    {action ? <div style={{ marginTop: '0.25rem' }}>{action}</div> : null}
  </div>
);
